import {useLocation, useNavigate, useParams} from "react-router-dom";
import {useShipmentCancelMutation, useShipmentGetQuery} from "../../app/api/api";
import QueryErrorAlert from "../../components/QueryErrorAlert";
import React, {useMemo} from "react";
import Page, {Content, ContentTitle, LabeledSection, LabeledValue, TitleBarButton} from "../../components/Layout/Page";
import {useTranslation} from "react-i18next";
import {formatTimestamp} from "../../utils/datetime";
import {
    Accessory,
    AccessoryField,
    ItemSerialNumber,
    ItemSerialNumberField,
    Shipment,
    ShipmentDevice,
    ShipmentDeviceField,
    ShipmentField,
    ShipmentStatus,
} from "../../app/api/model/shipment";
import {List, ListItem, Typography} from "@mui/material";
import TabSection from "../../components/Layout/Page/TabSection";
import SimpleTable from "../../components/ExTable/SimpleTable";
import {DeleteForever} from "@mui/icons-material";
import {useModal} from "../../hooks/useModal";
import {getStatusLabel} from "../orders/helpers";
import {EMPTY_UI} from "../../variables";
import CancelDialog from "../../components/Dialog/CancelDialog";
import {ColumnDef} from "../../components/ExTable";
import {TranslationContext} from "../../utils/getTranslationKey";
import {PermissionLevel} from "../../app/api/model/user";

export default function ShipmentDetails() {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const shipmentId = parseInt(params.shipmentId ?? "");
    const {data: shipment, error} = useShipmentGetQuery(shipmentId);
    const [shipmentCancel] = useShipmentCancelMutation();

    const {t} = useTranslation();
    const cancelShipmentModal = useModal(false);

    const initialTab = useMemo(() => {
        const fromHash = Number.parseInt((location.hash || "").replace("#", ""), 10);
        return Number.isFinite(fromHash) && fromHash >= 0 ? fromHash : 0;
    }, [location.hash]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        navigate(`#${newValue}`, {replace: true});
    };

    if (error) {
        return <QueryErrorAlert error={error} />;
    }

    const getSimiccdsList = (simiccids?: string[]) => {
        if (simiccids) {
            return (
                <List>
                    {simiccids.map((id: string) => (
                        <ListItem key={id}>{id}</ListItem>
                    ))}
                </List>
            );
        } else {
            return <Typography>{t("shipment_noSimiccdsAssigned")}</Typography>;
        }
    };

    const buttons: TitleBarButton[] = [];
    const notCanceled = shipment?.status !== ShipmentStatus.CANCELED;

    if (notCanceled && shipment && [ShipmentStatus.NEW, ShipmentStatus.CONFIRMED].indexOf(shipment.status) >= 0) {
        buttons.push({
            key: "cancel",
            tooltip: "action_cancelShipment",
            disabled: false,
            minRequiredPermissionToSee: PermissionLevel.MANAGER,
            callback: () => cancelShipmentModal.setIsOpen(true),
            icon: <DeleteForever />,
        });
    }

    const getItems = (items: ShipmentDevice[] | Accessory[] = []) => {
        if (items.length) {
            const isDevice = (entity: ShipmentDevice | Accessory): entity is ShipmentDevice =>
                (entity as ShipmentDevice).type !== undefined;

            const itemsColumnsDef = [
                {
                    fieldName: isDevice(items[0]) ? ShipmentDeviceField.TYPE : AccessoryField.ARTICLE,
                    label: t("generic_type"),
                    sortable: false,
                },
                {
                    fieldName: isDevice(items[0]) ? ShipmentDeviceField.QUANTITY : AccessoryField.QUANTITY,
                    label: t("generic_quantity"),
                    sortable: false,
                },
            ] as ColumnDef<ShipmentDevice | Accessory>[];
            return isDevice(items[0]) ? (
                <SimpleTable columnDefs={itemsColumnsDef} entries={items as ShipmentDevice[]} />
            ) : (
                <SimpleTable columnDefs={itemsColumnsDef} entries={items as Accessory[]} />
            );
        } else {
            return <Typography>{t("generic_noEntries")}</Typography>;
        }
    };

    const getSerialNumbers = (serialNumbers: ItemSerialNumber[] = []) => {
        if (serialNumbers) {
            const itemsColumnsDef = [
                {
                    fieldName: ItemSerialNumberField.LEO_ID,
                    label: t("generic_leoId"),
                    sortable: false,
                },
                {
                    fieldName: ItemSerialNumberField.SERIAL_NUMBER,
                    label: t("generic_serialNumber"),
                    sortable: false,
                },
            ] as ColumnDef<ItemSerialNumber>[];

            return <SimpleTable columnDefs={itemsColumnsDef} entries={serialNumbers} />;
        } else {
            return <Typography>{t("generic_noEntries")}</Typography>;
        }
    };

    return (
        <Page>
            <ContentTitle
                titleKey="generic_shipment"
                subtitle={shipment?.[ShipmentField.SHIPMENT_NUMBER]}
                entry={shipment}
                buttons={buttons}
                toolEntries={[
                    {
                        translationKey: "shipment_shippedAt",
                        fieldName: ShipmentField.SHIPPED_AT,
                        render: formatTimestamp,
                    },
                ]}
            />
            {shipment && (
                <>
                    <TabSection
                        title={t("generic_shipment")}
                        initialTab={initialTab}
                        onChange={handleTabChange}
                        tabs={[
                            {
                                label: t("tab_general"),
                                content: (
                                    <Content columns={2}>
                                        <LabeledSection title={t("shipment_generalInformation")}>
                                            <LabeledValue
                                                label={t("shipments_shipmentNumber")}
                                                content={shipment.shipmentNumber}
                                            />
                                            <LabeledValue
                                                label={t("generic_status")}
                                                content={
                                                    getStatusLabel(
                                                        shipment.status,
                                                        t,
                                                        TranslationContext.SHIPMENT_STATUS
                                                    )?.node
                                                }
                                            />
                                            <LabeledValue
                                                label={t("order_orderNumber")}
                                                content={shipment.orderNumber}
                                            />
                                            <LabeledValue
                                                label={t("shipment_reference")}
                                                content={shipment.reference}
                                            />

                                            <LabeledValue
                                                label={t("generic_createdAt")}
                                                content={formatTimestamp(shipment.createdAt)}
                                            />
                                            <LabeledValue
                                                label={t("generic_updatedAt")}
                                                content={formatTimestamp(shipment.updatedAt)}
                                            />
                                            <LabeledValue
                                                label={t("shipment_confirmedAt")}
                                                content={formatTimestamp(shipment.confirmedAt)}
                                            />
                                        </LabeledSection>
                                        <LabeledSection title={t("generic_customer")}>
                                            <LabeledValue
                                                label={t("generic_customerName")}
                                                content={shipment.customer?.name}
                                            />
                                            <LabeledValue
                                                label={t("generic_organizationId")}
                                                content={shipment.customer?.organizationId}
                                            />
                                            <LabeledValue
                                                label={t("generic_customerNumber")}
                                                content={shipment.customer?.customerNumber}
                                            />
                                        </LabeledSection>
                                        {!notCanceled && (
                                            <LabeledSection title={t("generic_cancelReason")} xs={12}>
                                                <Typography variant={"body1"}>
                                                    {shipment.cancelReason || EMPTY_UI}
                                                </Typography>
                                            </LabeledSection>
                                        )}
                                    </Content>
                                ),
                            },
                            {
                                label: t("tab_recipientDelivery"),
                                content: (
                                    <Content columns={2}>
                                        <LabeledSection title={t("order_shippingRecipient")}>
                                            <LabeledValue
                                                label={t("shipments_recipient")}
                                                content={`${shipment?.shippingRecipient.contact.firstName} ${shipment?.shippingRecipient.contact.lastName}`}
                                            />
                                            <LabeledValue
                                                label={t("generic_email")}
                                                content={shipment.shippingRecipient.contact.email}
                                            />
                                            <LabeledValue
                                                label={t("generic_phone")}
                                                content={shipment.shippingRecipient.contact.phone}
                                            />
                                            <LabeledValue
                                                label={t("generic_company")}
                                                content={shipment?.shippingRecipient.companyName}
                                            />
                                            <LabeledValue
                                                label={t("generic_street")}
                                                content={`${shipment?.shippingRecipient.address.street} ${shipment?.shippingRecipient.address.houseNumber}`}
                                            />
                                            <LabeledValue
                                                label={t("generic_addressAddition")}
                                                content={`${shipment?.shippingRecipient.address?.addressAddition}`}
                                            />
                                            <LabeledValue
                                                label={t("generic_addressPostalCode")}
                                                content={`${shipment?.shippingRecipient.address.postalCode}`}
                                            />
                                            <LabeledValue
                                                label={t("generic_city")}
                                                content={`${shipment?.shippingRecipient.address.city}`}
                                            />
                                            <LabeledValue
                                                label={t("generic_country")}
                                                content={`${shipment?.shippingRecipient.address.country}`}
                                            />
                                        </LabeledSection>
                                        <LabeledSection title={t("generic_delivery")}>
                                            <LabeledValue
                                                label={t("shipment_trackingNumber")}
                                                content={shipment.shippingInfo?.trackingNumber}
                                            />
                                            <LabeledValue
                                                label={t("shipment_shipper")}
                                                content={shipment.shippingInfo?.shipper}
                                            />
                                            <LabeledValue
                                                label={t("shipment_shippingTime")}
                                                content={formatTimestamp(shipment.shippingInfo?.shippingTime)}
                                            />
                                            <LabeledValue
                                                label={t("shipment_deliveryReference")}
                                                content={shipment.deliveryReference}
                                            />
                                            <LabeledValue
                                                label={t("shipment_shippedAt")}
                                                content={formatTimestamp(shipment.shippedAt)}
                                            />
                                            <LabeledValue
                                                label={t("shipment_deliveredAt")}
                                                content={formatTimestamp(shipment.deliveredAt)}
                                            />
                                            <LabeledValue label={t("shipment_receiver")} content={shipment.receiver} />
                                        </LabeledSection>
                                    </Content>
                                ),
                            },
                            {
                                label: t("tab_itemsDevices"),
                                content: (
                                    <Content columns={2}>
                                        <LabeledSection title={t("generic_simiccds")}>
                                            {getSimiccdsList(shipment.simiccids)}
                                        </LabeledSection>
                                        <LabeledSection title={t("order_items")}>
                                            {getItems(shipment.accessories)}
                                        </LabeledSection>
                                        <LabeledSection title={t("generic_devices")}>
                                            {getItems(shipment.devices)}
                                        </LabeledSection>
                                        <LabeledSection title={t("device_serialNumber")} xs={12}>
                                            {getSerialNumbers(shipment.serialNumbers)}
                                        </LabeledSection>
                                    </Content>
                                ),
                            },
                        ]}
                    />
                    <CancelDialog<Shipment>
                        entity={shipment}
                        isOpen={cancelShipmentModal.isOpen}
                        onSuccess={cancelShipmentModal.onClose}
                        onClose={cancelShipmentModal.onClose}
                        title={t("shipment_cancelShipment")}
                        bodyText={t("shipment_cancelInformation")}
                        titleCancel={t("action_cancelShipment")}
                        onSubmit={(requestData) => shipmentCancel(requestData).unwrap()}
                    />
                </>
            )}
        </Page>
    );
}
